import { EVENT_STATUS } from '../constants';
import { UnscheduledEvent } from '../types';
import { createLogger } from '../utils/logger';
import { fetchEventById, updateEvent } from './eventService';

const logger = createLogger('ClearActiveEvents');
const CLEAR_BATCH_SIZE: number = 5;

/** Result of marking a set of active events as deleted. */
export interface ClearActiveEventsResult {
  deletedEventIds: string[];
  failedEventIds: string[];
}

async function deleteActiveEvent(eventId: string): Promise<boolean> {
  try {
    const event: UnscheduledEvent | null = await fetchEventById(eventId);
    if (!event) return false;

    const isActive: boolean =
      event.status === EVENT_STATUS.IN_WORK || event.status === EVENT_STATUS.PENDING;
    if (!isActive) return false;

    const updated: UnscheduledEvent | null = await updateEvent({
      ...event,
      status: EVENT_STATUS.DELETED,
    });
    return updated !== null;
  } catch (error: unknown) {
    logger.error('Active event could not be deleted', {
      error: error instanceof Error ? error.message : 'Unknown error',
      eventId,
    });
    return false;
  }
}

/**
 * Loads full event records before marking them Deleted so list projections never
 * replace fields omitted from the homepage payload. Requests run in small batches
 * to avoid overwhelming the API while clearing a training environment.
 */
export async function clearActiveEvents(
  events: UnscheduledEvent[],
): Promise<ClearActiveEventsResult> {
  const result: ClearActiveEventsResult = { deletedEventIds: [], failedEventIds: [] };
  for (let index: number = 0; index < events.length; index += CLEAR_BATCH_SIZE) {
    const batch: UnscheduledEvent[] = events.slice(index, index + CLEAR_BATCH_SIZE);
    try {
      const outcomes: boolean[] = await Promise.all(
        batch.map((event: UnscheduledEvent): Promise<boolean> => deleteActiveEvent(event.eventId)),
      );
      batch.forEach((event: UnscheduledEvent, batchIndex: number): void => {
        const destination: string[] = outcomes[batchIndex]
          ? result.deletedEventIds
          : result.failedEventIds;
        destination.push(event.eventId);
      });
    } catch (error: unknown) {
      logger.error('Clear batch failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        batchSize: batch.length,
      });
      result.failedEventIds.push(...batch.map((event: UnscheduledEvent): string => event.eventId));
    }
  }
  return result;
}
